import React, { useState, useEffect } from 'react';
import { api, formatCurrency, formatDate, investmentTypes, getCategoryLabel } from '../lib/utils';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  TrendingUp, Plus, IndianRupee, PiggyBank, Landmark, ArrowUpRight, ArrowDownRight,
  Search, Pencil, Trash2, RefreshCw, Pause, Play, Wallet, Building
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const COLORS = ['#18181B', '#52525B', '#71717A', '#A1A1AA', '#22C55E', '#3B82F6', '#F59E0B'];

export default function Investments() {
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const [investments, setInvestments] = useState([]);
  const [loans, setLoans] = useState([]);
  const [sips, setSips] = useState([]);
  const [bankBalances, setBankBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addInvestmentOpen, setAddInvestmentOpen] = useState(false);
  const [addLoanOpen, setAddLoanOpen] = useState(false);
  const [addSipOpen, setAddSipOpen] = useState(false);
  const [addBalanceOpen, setAddBalanceOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [editInvestment, setEditInvestment] = useState(null);
  const [editLoan, setEditLoan] = useState(null);
  const [editSip, setEditSip] = useState(null);
  const [editBalance, setEditBalance] = useState(null);
  const [activeTab, setActiveTab] = useState('investments');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [invRes, loansRes, sipsRes, balRes] = await Promise.all([
        api.get('/investments'), api.get('/loans'),
        api.get('/sips').catch(() => ({ data: [] })),
        api.get('/bank-balances').catch(() => ({ data: [] }))
      ]);
      setInvestments(invRes.data);
      setLoans(loansRes.data);
      setSips(sipsRes.data);
      setBankBalances(balRes.data);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const totalInvested = investments.reduce((s, i) => s + i.amount, 0);
  const totalCurrentValue = investments.reduce((s, i) => s + i.current_value, 0);
  const totalGain = totalCurrentValue - totalInvested;
  const gainPercent = totalInvested > 0 ? (totalGain / totalInvested) * 100 : 0;
  const totalLoanBalance = loans.reduce((s, l) => s + l.outstanding_balance, 0);
  const totalBankBalance = bankBalances.reduce((s, b) => s + b.balance, 0);
  const netWorth = totalCurrentValue + totalBankBalance - totalLoanBalance;
  const monthlySipOutflow = sips.filter(s => s.is_active).reduce((sum, s) => sum + s.amount, 0);

  const investmentsByType = investments.reduce((acc, inv) => {
    if (!acc[inv.type]) acc[inv.type] = 0;
    acc[inv.type] += inv.current_value;
    return acc;
  }, {});
  const chartData = Object.entries(investmentsByType).map(([type, value]) => ({ name: getCategoryLabel(type, investmentTypes), value }));

  const handleDeleteInvestment = async (inv) => {
    if (!window.confirm(`Delete ${inv.name}?`)) return;
    try { await api.delete(`/investments/${inv.investment_id}`); toast.success('Deleted'); fetchData(); }
    catch { toast.error('Failed'); }
  };
  const handleDeleteLoan = async (loan) => {
    if (!window.confirm(`Delete ${loan.name}?`)) return;
    try { await api.delete(`/loans/${loan.loan_id}`); toast.success('Deleted'); fetchData(); }
    catch { toast.error('Failed'); }
  };
  const handleDeleteSip = async (sip) => {
    if (!window.confirm(`Delete SIP ${sip.name}?`)) return;
    try { await api.delete(`/sips/${sip.sip_id}`); toast.success('Deleted'); fetchData(); }
    catch { toast.error('Failed'); }
  };
  const handleToggleSip = async (sip) => {
    try { await api.post(`/sips/${sip.sip_id}/toggle`); toast.success(sip.is_active ? 'Paused' : 'Activated'); fetchData(); }
    catch { toast.error('Failed'); }
  };
  const handleRecordSipDebit = async (sip) => {
    if (!window.confirm(`Record SIP debit of ${formatCurrency(sip.amount)} for ${sip.name}?`)) return;
    try { await api.post(`/sips/${sip.sip_id}/record-debit`); toast.success('SIP debit recorded'); fetchData(); }
    catch { toast.error('Failed'); }
  };
  const handleDeleteBalance = async (b) => {
    if (!window.confirm(`Delete ${b.bank_name}?`)) return;
    try { await api.delete(`/bank-balances/${b.balance_id}`); toast.success('Deleted'); fetchData(); }
    catch { toast.error('Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>;

  return (
    <div className="max-w-[1400px] mx-auto" data-testid="investments-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-zinc-900">Investments & Net Worth</h1>
          <p className="text-zinc-500 mt-1">Track wealth allocation and growth</p>
        </div>
      </div>

      {/* Net Worth Summary */}
      <Card className="roch-card mb-8">
        <CardContent className="py-6">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-6">
            <div><div className="metric-label">Total Invested</div><div className="text-xl font-semibold currency">{formatCurrency(totalInvested)}</div></div>
            <div><div className="metric-label">Current Value</div><div className="text-xl font-semibold currency">{formatCurrency(totalCurrentValue)}</div></div>
            <div><div className="metric-label">Returns</div><div className={`text-xl font-semibold flex items-center gap-1 ${totalGain >= 0 ? 'text-green-600' : 'text-red-600'}`}>{totalGain >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}{formatCurrency(Math.abs(totalGain))}<span className="text-sm">({gainPercent.toFixed(1)}%)</span></div></div>
            <div><div className="metric-label">Bank Balance</div><div className="text-xl font-semibold text-blue-600 currency">{formatCurrency(totalBankBalance)}</div></div>
            <div><div className="metric-label">Loan Balance</div><div className="text-xl font-semibold text-red-600 currency">{formatCurrency(totalLoanBalance)}</div></div>
            <div><div className="metric-label">Net Worth</div><div className={`text-2xl font-bold currency ${netWorth >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(netWorth)}</div></div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="investments">Investments</TabsTrigger>
          <TabsTrigger value="sips">SIPs</TabsTrigger>
          <TabsTrigger value="bank-balances">Bank Balances</TabsTrigger>
          <TabsTrigger value="loans">Loans</TabsTrigger>
        </TabsList>

        {/* INVESTMENTS TAB */}
        <TabsContent value="investments">
          <div className="flex items-center justify-between mb-4">
            <div className="relative w-64"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" /><Input placeholder="Search investments..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9 h-9" data-testid="investments-search" /></div>
            {isOwner && <Button onClick={() => { setEditInvestment(null); setAddInvestmentOpen(true); }} className="rounded-xl" data-testid="add-investment-btn"><Plus className="w-4 h-4 mr-2" />Add Investment</Button>}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="roch-card">
              <CardHeader className="pb-2"><CardTitle className="text-base font-medium">Asset Allocation</CardTitle></CardHeader>
              <CardContent>
                {chartData.length === 0 ? <div className="text-center py-8 text-sm text-zinc-500">No investments recorded</div> : (
                  <div className="flex items-center">
                    <div className="w-[180px] h-[180px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart><Pie data={chartData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2} dataKey="value">{chartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip formatter={(v) => formatCurrency(v)} /></PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="flex-1 space-y-2 ml-4">
                      {chartData.map((item, i) => (
                        <div key={item.name} className="flex items-center justify-between text-sm"><div className="flex items-center gap-2"><div className="w-3 h-3 rounded-sm" style={{ backgroundColor: COLORS[i % COLORS.length] }} /><span className="text-zinc-600">{item.name}</span></div><span className="font-medium">{formatCurrency(item.value)}</span></div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            <Card className="roch-card lg:col-span-2">
              <CardContent className="p-0">
                {investments.length === 0 ? <div className="empty-state"><div className="empty-state-icon"><PiggyBank className="w-6 h-6" /></div><h3 className="font-medium text-zinc-900 mb-1">No investments yet</h3></div> : (
                  <table className="roch-table">
                    <thead><tr><th>Name</th><th>Type</th><th>Date</th><th className="text-right">Invested</th><th className="text-right">Current</th><th className="text-right">Returns</th><th className="w-20"></th></tr></thead>
                    <tbody>
                      {investments.filter(inv => !searchQuery || inv.name?.toLowerCase().includes(searchQuery.toLowerCase()) || inv.type?.toLowerCase().includes(searchQuery.toLowerCase())).map((inv) => {
                        const ret = inv.current_value - inv.amount;
                        const retPct = inv.amount > 0 ? (ret / inv.amount) * 100 : 0;
                        return (
                          <tr key={inv.investment_id}>
                            <td className="font-medium">{inv.name}</td>
                            <td><span className="badge badge-default">{getCategoryLabel(inv.type, investmentTypes)}</span></td>
                            <td className="text-zinc-500">{formatDate(inv.date)}</td>
                            <td className="text-right">{formatCurrency(inv.amount)}</td>
                            <td className="text-right font-medium">{formatCurrency(inv.current_value)}</td>
                            <td className={`text-right font-medium ${ret >= 0 ? 'text-green-600' : 'text-red-600'}`}>{ret >= 0 ? '+' : ''}{formatCurrency(ret)}<span className="text-xs ml-1">({retPct.toFixed(1)}%)</span></td>
                            <td>{isOwner && <div className="flex items-center gap-1"><Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditInvestment(inv); setAddInvestmentOpen(true); }}><Pencil className="w-3.5 h-3.5" /></Button><Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDeleteInvestment(inv)}><Trash2 className="w-3.5 h-3.5" /></Button></div>}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* SIPs TAB */}
        <TabsContent value="sips">
          <div className="flex items-center justify-between mb-4">
            <div>
              {monthlySipOutflow > 0 && <p className="text-sm text-zinc-500">Monthly SIP outflow: <span className="font-semibold text-zinc-800">{formatCurrency(monthlySipOutflow)}</span></p>}
            </div>
            {isOwner && <Button onClick={() => { setEditSip(null); setAddSipOpen(true); }} className="rounded-xl" data-testid="add-sip-btn"><Plus className="w-4 h-4 mr-2" />Add SIP</Button>}
          </div>
          <Card className="roch-card">
            <CardContent className="p-0">
              {sips.length === 0 ? <div className="empty-state"><div className="empty-state-icon"><RefreshCw className="w-6 h-6" /></div><h3 className="font-medium text-zinc-900 mb-1">No SIPs yet</h3><p className="text-sm text-zinc-500">Add recurring investments like SIPs and monthly debits</p></div> : (
                <table className="roch-table">
                  <thead><tr><th>Name</th><th>Type</th><th className="text-right">Amount</th><th>Debit Day</th><th>Next Debit</th><th className="text-right">Total Invested</th><th>Status</th><th className="w-32"></th></tr></thead>
                  <tbody>
                    {sips.map((sip) => (
                      <tr key={sip.sip_id}>
                        <td className="font-medium">{sip.name}</td>
                        <td><span className="badge badge-default">{getCategoryLabel(sip.investment_type, investmentTypes)}</span></td>
                        <td className="text-right font-medium">{formatCurrency(sip.amount)}</td>
                        <td className="text-zinc-500">{sip.day_of_month}{['st','nd','rd'][sip.day_of_month-1] || 'th'} of month</td>
                        <td className="text-zinc-500">{sip.next_debit_date ? formatDate(sip.next_debit_date) : '-'}</td>
                        <td className="text-right">{formatCurrency(sip.total_invested)}</td>
                        <td><span className={`badge ${sip.is_active ? 'badge-success' : 'badge-warning'}`}>{sip.is_active ? 'Active' : 'Paused'}</span></td>
                        <td>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-green-600" onClick={() => handleRecordSipDebit(sip)} title="Record debit"><IndianRupee className="w-3.5 h-3.5" /></Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleToggleSip(sip)} title={sip.is_active ? 'Pause' : 'Resume'}>{sip.is_active ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}</Button>
                            {isOwner && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditSip(sip); setAddSipOpen(true); }}><Pencil className="w-3.5 h-3.5" /></Button>}
                            {isOwner && <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDeleteSip(sip)}><Trash2 className="w-3.5 h-3.5" /></Button>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* BANK BALANCES TAB */}
        <TabsContent value="bank-balances">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-zinc-500">Total across all accounts: <span className="font-semibold text-zinc-800">{formatCurrency(totalBankBalance)}</span></p>
            {isOwner && <Button onClick={() => { setEditBalance(null); setAddBalanceOpen(true); }} className="rounded-xl" data-testid="add-balance-btn"><Plus className="w-4 h-4 mr-2" />Add Bank Account</Button>}
          </div>
          <Card className="roch-card">
            <CardContent className="p-0">
              {bankBalances.length === 0 ? <div className="empty-state"><div className="empty-state-icon"><Building className="w-6 h-6" /></div><h3 className="font-medium text-zinc-900 mb-1">No bank accounts</h3><p className="text-sm text-zinc-500">Add your bank balances to track net worth accurately</p></div> : (
                <table className="roch-table">
                  <thead><tr><th>Bank Name</th><th>Account Type</th><th className="text-right">Balance</th><th>As of</th><th>Notes</th><th className="w-20"></th></tr></thead>
                  <tbody>
                    {bankBalances.map((b) => (
                      <tr key={b.balance_id}>
                        <td className="font-medium">{b.bank_name}</td>
                        <td><span className="badge badge-default capitalize">{b.account_type}</span></td>
                        <td className="text-right font-semibold text-blue-600">{formatCurrency(b.balance)}</td>
                        <td className="text-zinc-500">{formatDate(b.as_of_date)}</td>
                        <td className="text-zinc-500 text-sm">{b.notes || '-'}</td>
                        <td>{isOwner && <div className="flex items-center gap-1"><Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditBalance(b); setAddBalanceOpen(true); }}><Pencil className="w-3.5 h-3.5" /></Button><Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDeleteBalance(b)}><Trash2 className="w-3.5 h-3.5" /></Button></div>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* LOANS TAB */}
        <TabsContent value="loans">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-zinc-500">Total outstanding: <span className="font-semibold text-red-600">{formatCurrency(totalLoanBalance)}</span></p>
            {isOwner && <Button onClick={() => { setEditLoan(null); setAddLoanOpen(true); }} className="rounded-xl" data-testid="add-loan-btn"><Plus className="w-4 h-4 mr-2" />Add Loan</Button>}
          </div>
          <Card className="roch-card">
            <CardContent className="p-0">
              {loans.length === 0 ? <div className="empty-state"><div className="empty-state-icon"><Landmark className="w-6 h-6" /></div><h3 className="font-medium text-zinc-900 mb-1">No loans</h3></div> : (
                <table className="roch-table">
                  <thead><tr><th>Name</th><th>Start Date</th><th className="text-right">Loan Amount</th><th className="text-right">Rate</th><th className="text-right">EMI</th><th className="text-right">Outstanding</th><th className="w-20"></th></tr></thead>
                  <tbody>
                    {loans.map((loan) => (
                      <tr key={loan.loan_id}>
                        <td className="font-medium">{loan.name}</td>
                        <td className="text-zinc-500">{formatDate(loan.start_date)}</td>
                        <td className="text-right">{formatCurrency(loan.loan_amount)}</td>
                        <td className="text-right">{loan.interest_rate}%</td>
                        <td className="text-right">{formatCurrency(loan.emi_amount)}</td>
                        <td className="text-right font-medium text-red-600">{formatCurrency(loan.outstanding_balance)}</td>
                        <td>{isOwner && <div className="flex items-center gap-1"><Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditLoan(loan); setAddLoanOpen(true); }}><Pencil className="w-3.5 h-3.5" /></Button><Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDeleteLoan(loan)}><Trash2 className="w-3.5 h-3.5" /></Button></div>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <AddInvestmentDialog open={addInvestmentOpen} onOpenChange={(v) => { setAddInvestmentOpen(v); if (!v) setEditInvestment(null); }} onSuccess={fetchData} editItem={editInvestment} />
      <AddLoanDialog open={addLoanOpen} onOpenChange={(v) => { setAddLoanOpen(v); if (!v) setEditLoan(null); }} onSuccess={fetchData} editItem={editLoan} />
      <AddSipDialog open={addSipOpen} onOpenChange={(v) => { setAddSipOpen(v); if (!v) setEditSip(null); }} onSuccess={fetchData} editItem={editSip} />
      <AddBalanceDialog open={addBalanceOpen} onOpenChange={(v) => { setAddBalanceOpen(v); if (!v) setEditBalance(null); }} onSuccess={fetchData} editItem={editBalance} />
    </div>
  );
}

function AddInvestmentDialog({ open, onOpenChange, onSuccess, editItem }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ type: '', name: '', amount: '', current_value: '', date: new Date().toISOString().split('T')[0], notes: '' });

  useEffect(() => {
    if (editItem) setForm({ type: editItem.type || '', name: editItem.name || '', amount: String(editItem.amount || ''), current_value: String(editItem.current_value || ''), date: editItem.date || '', notes: editItem.notes || '' });
    else setForm({ type: '', name: '', amount: '', current_value: '', date: new Date().toISOString().split('T')[0], notes: '' });
  }, [editItem, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.type || !form.name || !form.amount) { toast.error('Fill required fields'); return; }
    setLoading(true);
    try {
      const payload = { ...form, amount: parseFloat(form.amount), current_value: parseFloat(form.current_value || form.amount) };
      if (editItem) { await api.put(`/investments/${editItem.investment_id}`, payload); toast.success('Updated'); }
      else { await api.post('/investments', payload); toast.success('Recorded'); }
      onOpenChange(false); onSuccess();
    } catch { toast.error('Failed'); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader><DialogTitle>{editItem ? 'Edit' : 'Add'} Investment</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div><Label>Type</Label><Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}><SelectTrigger className="mt-1.5"><SelectValue placeholder="Select type" /></SelectTrigger><SelectContent>{investmentTypes.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Name</Label><Input placeholder="e.g., HDFC Flexi Cap Fund" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Invested Amount</Label><div className="relative mt-1.5"><IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" /><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="pl-9" /></div></div>
            <div><Label>Current Value</Label><div className="relative mt-1.5"><IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" /><Input type="number" placeholder="Same as invested" value={form.current_value} onChange={(e) => setForm({ ...form, current_value: e.target.value })} className="pl-9" /></div></div>
          </div>
          <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Notes</Label><Input placeholder="Optional notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" /></div>
          <Button type="submit" className="w-full" disabled={loading}>{loading ? 'Saving...' : editItem ? 'Update' : 'Record Investment'}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AddLoanDialog({ open, onOpenChange, onSuccess, editItem }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', loan_amount: '', interest_rate: '', emi_amount: '', start_date: new Date().toISOString().split('T')[0], tenure_months: '' });

  useEffect(() => {
    if (editItem) setForm({ name: editItem.name || '', loan_amount: String(editItem.loan_amount || ''), interest_rate: String(editItem.interest_rate || ''), emi_amount: String(editItem.emi_amount || ''), start_date: editItem.start_date || '', tenure_months: String(editItem.tenure_months || '') });
    else setForm({ name: '', loan_amount: '', interest_rate: '', emi_amount: '', start_date: new Date().toISOString().split('T')[0], tenure_months: '' });
  }, [editItem, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.loan_amount || !form.emi_amount) { toast.error('Fill required fields'); return; }
    setLoading(true);
    try {
      const payload = { ...form, loan_amount: parseFloat(form.loan_amount), interest_rate: parseFloat(form.interest_rate || 0), emi_amount: parseFloat(form.emi_amount), tenure_months: parseInt(form.tenure_months || 0) };
      if (editItem) { await api.put(`/loans/${editItem.loan_id}`, payload); toast.success('Updated'); }
      else { await api.post('/loans', payload); toast.success('Recorded'); }
      onOpenChange(false); onSuccess();
    } catch { toast.error('Failed'); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader><DialogTitle>{editItem ? 'Edit' : 'Add'} Loan</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div><Label>Name</Label><Input placeholder="e.g., Restaurant Setup Loan" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Loan Amount</Label><div className="relative mt-1.5"><IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" /><Input type="number" value={form.loan_amount} onChange={(e) => setForm({ ...form, loan_amount: e.target.value })} className="pl-9" /></div></div>
            <div><Label>Interest Rate (%)</Label><Input type="number" step="0.1" value={form.interest_rate} onChange={(e) => setForm({ ...form, interest_rate: e.target.value })} className="mt-1.5" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>EMI Amount</Label><div className="relative mt-1.5"><IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" /><Input type="number" value={form.emi_amount} onChange={(e) => setForm({ ...form, emi_amount: e.target.value })} className="pl-9" /></div></div>
            <div><Label>Tenure (months)</Label><Input type="number" value={form.tenure_months} onChange={(e) => setForm({ ...form, tenure_months: e.target.value })} className="mt-1.5" /></div>
          </div>
          <div><Label>Start Date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1.5" /></div>
          <Button type="submit" className="w-full" disabled={loading}>{loading ? 'Saving...' : editItem ? 'Update' : 'Record Loan'}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AddSipDialog({ open, onOpenChange, onSuccess, editItem }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ investment_type: '', name: '', amount: '', day_of_month: '1', start_date: new Date().toISOString().split('T')[0], notes: '' });

  useEffect(() => {
    if (editItem) setForm({ investment_type: editItem.investment_type || '', name: editItem.name || '', amount: String(editItem.amount || ''), day_of_month: String(editItem.day_of_month || '1'), start_date: editItem.start_date || '', notes: editItem.notes || '' });
    else setForm({ investment_type: '', name: '', amount: '', day_of_month: '1', start_date: new Date().toISOString().split('T')[0], notes: '' });
  }, [editItem, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.investment_type || !form.name || !form.amount) { toast.error('Fill required fields'); return; }
    setLoading(true);
    try {
      const payload = { ...form, amount: parseFloat(form.amount), day_of_month: parseInt(form.day_of_month) };
      if (editItem) { await api.put(`/sips/${editItem.sip_id}`, payload); toast.success('Updated'); }
      else { await api.post('/sips', payload); toast.success('Created'); }
      onOpenChange(false); onSuccess();
    } catch { toast.error('Failed'); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader><DialogTitle>{editItem ? 'Edit' : 'Add'} SIP / Recurring Investment</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div><Label>Investment Type</Label><Select value={form.investment_type} onValueChange={(v) => setForm({ ...form, investment_type: v })}><SelectTrigger className="mt-1.5"><SelectValue placeholder="Select type" /></SelectTrigger><SelectContent>{investmentTypes.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Name</Label><Input placeholder="e.g., Axis Bluechip SIP" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Monthly Amount</Label><div className="relative mt-1.5"><IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" /><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="pl-9" /></div></div>
            <div><Label>Debit Day of Month</Label><Input type="number" min="1" max="31" value={form.day_of_month} onChange={(e) => setForm({ ...form, day_of_month: e.target.value })} className="mt-1.5" /></div>
          </div>
          <div><Label>Start Date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Notes</Label><Input placeholder="Optional notes..." value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" /></div>
          <Button type="submit" className="w-full" disabled={loading}>{loading ? 'Saving...' : editItem ? 'Update' : 'Create SIP'}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AddBalanceDialog({ open, onOpenChange, onSuccess, editItem }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ bank_name: '', account_type: 'savings', balance: '', as_of_date: new Date().toISOString().split('T')[0], notes: '' });
  const accountTypes = ['savings', 'current', 'fd', 'rd', 'wallet'];

  useEffect(() => {
    if (editItem) setForm({ bank_name: editItem.bank_name || '', account_type: editItem.account_type || 'savings', balance: String(editItem.balance || ''), as_of_date: editItem.as_of_date || '', notes: editItem.notes || '' });
    else setForm({ bank_name: '', account_type: 'savings', balance: '', as_of_date: new Date().toISOString().split('T')[0], notes: '' });
  }, [editItem, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.bank_name || !form.balance) { toast.error('Fill required fields'); return; }
    setLoading(true);
    try {
      const payload = { ...form, balance: parseFloat(form.balance) };
      if (editItem) { await api.put(`/bank-balances/${editItem.balance_id}`, payload); toast.success('Updated'); }
      else { await api.post('/bank-balances', payload); toast.success('Added'); }
      onOpenChange(false); onSuccess();
    } catch { toast.error('Failed'); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader><DialogTitle>{editItem ? 'Edit' : 'Add'} Bank Balance</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div><Label>Bank Name</Label><Input placeholder="e.g., HDFC Bank" value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} className="mt-1.5" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Account Type</Label><Select value={form.account_type} onValueChange={(v) => setForm({ ...form, account_type: v })}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent>{accountTypes.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Balance</Label><div className="relative mt-1.5"><IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" /><Input type="number" value={form.balance} onChange={(e) => setForm({ ...form, balance: e.target.value })} className="pl-9" /></div></div>
          </div>
          <div><Label>As of Date</Label><Input type="date" value={form.as_of_date} onChange={(e) => setForm({ ...form, as_of_date: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Notes</Label><Input placeholder="Optional notes..." value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" /></div>
          <Button type="submit" className="w-full" disabled={loading}>{loading ? 'Saving...' : editItem ? 'Update' : 'Add Bank Balance'}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
